/**
 * Executes actions when a popup dialog is created.
 */
export const onPopupDialogCreated = () => {
    // Get the current scroll position
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    // Set the top position for a popup element to ensure it remains visible within a container.
    setPopupPosition();

    // Scroll to the previous scroll position smoothly
    window.scrollTo({
        top: scrollTop,
        behavior: 'smooth',
    });

    // Set the default category to 'Date' after a brief delay
    setTimeout(() => {
        let categoryDateFlag = setCategoryForIntegratedChart('Date');
        if (!categoryDateFlag) {
            setCategoryForIntegratedChart('Period Date');
        }

        // Show the popup dialog if available
        const popup = getLastIntegratedDialog();
        if (popup) {
            popup.classList.add('ag-dialog--visible');
        }
    }, 100);
};

/**
 * Sets the position of a popup by triggering mousedown, mousemove, and mouseup events programmatically.
 */
function setPopupPosition() {
    // Get the last integrated dialog (assuming this is a custom function)
    const popup = getLastIntegratedDialog();

    // If there is no popup, exit the function
    if (!popup) {
        return;
    }

    // Find the draggable element within the popup
    const draggableElement = popup.querySelector('.ag-panel-title-bar');

    // Get the current position and size of the popup
    const popupRect = popup.getBoundingClientRect();

    // Calculate the desired position for the popup
    const popupPath = getPopupTopPosition(popup);

    /**
     * Simulates a mouse event and dispatches it on the draggable element.
     * @param {string} type - The type of mouse event ('mousedown', 'mousemove', or 'mouseup').
     * @param {number} clientX - The X-coordinate of the mouse event.
     * @param {number} clientY - The Y-coordinate of the mouse event.
     */
    const simulateMouseEvent = (type, clientX, clientY) => {
        // Create a new MouseEvent with specified properties
        const event = new MouseEvent(type, {
            bubbles: true, // Allows the event to bubble up the DOM tree
            cancelable: true, // Allows the event to be canceled
            clientX, // X-coordinate
            clientY, // Y-coordinate
        });

        // Dispatch the event on the draggable element
        draggableElement.dispatchEvent(event);
    };

    // Simulate the mousedown event at the initial position
    simulateMouseEvent('mousedown', popupRect.left, popupRect.top);

    // Simulate the mousemove event at the desired position
    simulateMouseEvent('mousemove', popupPath.left, popupPath.top);

    // Simulate the mouseup event (no specific position needed)
    simulateMouseEvent('mouseup');
}

/**
 * Calculates and returns the top and left position for a popup element to center it vertically within the viewport.
 * @param {HTMLElement} popup - The popup element for which to calculate the position.
 * @returns {Object} An object with 'top' and 'left' properties representing the calculated positions.
 */
function getPopupTopPosition(popup) {
    // Get the height of the viewport
    const viewportHeight = window.innerHeight;

    // Get the dimensions of the popup element
    const popupRect = popup.getBoundingClientRect();
    const popupRectHeight = popupRect.height;

    // Calculate the fixed top position of the popup element to center it vertically
    const popupFixedTopPosition = (viewportHeight - popupRectHeight) / 2;

    // Get the current absolute left position of the popup
    let currentAbsoluteLeftPosition = parseFloat(popup.style.left);

    // Return an object with the calculated 'top' and 'left' positions
    return {
        top: popupFixedTopPosition,
        left: currentAbsoluteLeftPosition,
    };
}

/**
 * Sets the category for an integrated chart by triggering the corresponding radio button.
 * @param {string} category - The category to set (e.g., 'Date', 'Category', etc.).
 */
function setCategoryForIntegratedChart(category) {
    // Find and trigger the radio button corresponding to the specified category
    const categoryLabels = document.querySelectorAll(
        '#categoriesGroup .ag-input-field-label',
    );

    // Iterate through the radio button labels to find a match
    categoryLabels.forEach(function (label) {
        if (category === label.innerHTML) {
            // Click the label to select the corresponding radio button
            label.click();
            return true;
        }
    });
    return false;
}

/**
 * Retrieves the last integrated dialog element in the DOM.
 * @returns {HTMLElement|undefined} The last integrated dialog element or undefined if none found.
 */
function getLastIntegratedDialog() {
    // Find all elements with the class 'ag-popup-child' (assuming this class represents integrated dialogs)
    const popupList = document.querySelectorAll('.ag-popup-child');

    // If there are no elements with the class 'ag-popup-child', return undefined
    if (!popupList.length) {
        return;
    }

    // Return the last element in the list (last integrated dialog)
    return popupList[popupList.length - 1];
}
